// Auto-dismisses server-rendered flash messages, toast style.
//
// The flash partial is plain server-rendered HTML shared by the public and admin
// layouts, and it can appear on any page — so this cannot live in one pack's
// entrypoint. `entrypoints/flash.ts` is loaded by the partial itself, which means
// it is only ever fetched on a request that actually has a flash to show.

export type FlashKind = "notice" | "alert";

/**
 * How long each kind of flash stays before it starts fading.
 *
 * An error outlives a notice deliberately. A notice confirms something the user
 * just did and already knows about; an alert is often the only explanation they
 * will get for why a purchase did not go through, and re-reading it is not
 * possible once it is gone — the Rails flash is consumed by the request that
 * rendered it, so a refresh will not bring it back.
 */
export const FLASH_DISMISS_DELAYS_MS: Record<FlashKind, number> = {
  notice: 5000,
  alert: 8000,
};

/** Long enough to outlast Bootstrap's own .fade transition (150ms). */
export const FLASH_FADE_MS = 300;

export function flashKind(value: string | null | undefined): FlashKind {
  return value === "alert" ? "alert" : "notice";
}

export function flashDismissDelayMs(value: string | null | undefined): number {
  return FLASH_DISMISS_DELAYS_MS[flashKind(value)];
}

/**
 * Fades an element out and then removes it.
 *
 * Removing the `show` class is what Bootstrap's own `.fade` transition keys off,
 * so the visual behaviour matches every other dismissible alert in the app
 * without needing Bootstrap's JS — which is not loaded in every pack, and the
 * flash can appear on any page.
 */
export function dismissFlash(element: HTMLElement): void {
  if (!element.isConnected) return;
  element.classList.remove("show");
  window.setTimeout(() => element.remove(), FLASH_FADE_MS);
}

/**
 * Wires auto-dismissal and the close button for every flash in `root`.
 *
 * Hovering or focusing within a flash cancels its timer: someone who is still
 * reading — or who has tabbed to the close button — should not have it vanish
 * mid-sentence. Once cancelled it stays until dismissed by hand, which is the
 * safe direction to fail in for a message about money.
 */
export function initFlashAutoDismiss(root: ParentNode = document): void {
  const flashes = root.querySelectorAll<HTMLElement>("[data-flash-kind]");

  flashes.forEach((element) => {
    let timer: number | undefined = window.setTimeout(
      () => dismissFlash(element),
      flashDismissDelayMs(element.dataset.flashKind),
    );

    const cancel = () => {
      if (timer === undefined) return;
      window.clearTimeout(timer);
      timer = undefined;
    };

    element.addEventListener("mouseenter", cancel);
    element.addEventListener("focusin", cancel);

    element.querySelector<HTMLElement>("[data-flash-close]")?.addEventListener("click", () => {
      cancel();
      dismissFlash(element);
    });
  });
}
